import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Check, CheckCheck, Home, ClipboardList, Users, Sparkles, X, MessageCircle, CalendarClock, ShieldAlert, Loader2, Landmark, Newspaper } from 'lucide-react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { submitShareAppeal } from '../../lib/share-compliance';
import { useAuth } from '../../contexts/AuthContext';
import { useDesktopNotification } from '../../hooks/useDesktopNotification';
import { getNotificationRoute } from '../../lib/notification-route';

interface Notification {
  id: string;
  title: string;
  body: string | null;
  type: string;
  target_type: string | null;
  target_id: string | null;
  is_read: boolean;
  created_at: string;
  dismissed_at?: string | null;
}

/** A queue that needs action (e.g. "Tickets abiertos: 9"), linking to its screen. */
export interface AttentionItem {
  label: string;
  count: number;
  path: string;
}

interface NotificationDropdownProps {
  theme: 'light' | 'dark' | 'high-contrast';
  // Super admins only: when set, the bell shows the total of these queues
  // instead of the unread-notification count, and the dropdown lists them.
  attention?: AttentionItem[];
}

const typeIcons: Record<string, React.ReactNode> = {
  financing_application: <Landmark size={16} className="text-indigo-400" />,
  new_match: <Users size={16} className="text-purple-400" />,
  new_lead: <Sparkles size={16} className="text-emerald-400" />,
  lead_assigned: <Sparkles size={16} className="text-emerald-400" />,
  reminder_due: <Bell size={16} className="text-amber-400" />,
  like: <Home size={16} className="text-rose-400" />,
  sharing_received: <ClipboardList size={16} className="text-blue-400" />,
  wa_message: <MessageCircle size={16} className="text-emerald-400" />,
  wa_health: <MessageCircle size={16} className="text-amber-400" />,
  // Clock, not a check: a vitrina booking is a REQUEST awaiting the agent's
  // confirmation (afe7726), so it must not read as an already-confirmed cita.
  appointment_request: <CalendarClock size={16} className="text-amber-400" />,
  share_compliance: <ShieldAlert size={16} className="text-amber-400" />,
  blog_post_pending: <Newspaper size={16} className="text-sky-400" />,
  novedad: <Newspaper size={16} className="text-indigo-400" />,
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Hace ${days}d`;
  return new Date(dateStr).toLocaleDateString('es-GT', { day: '2-digit', month: 'short' });
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ theme, attention }) => {
  const isLight = theme !== 'dark';
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { fire: fireDesktopNotification } = useDesktopNotification();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [appealingId, setAppealingId] = useState<string | null>(null);

  const pendingItems = (attention ?? []).filter(a => a.count > 0);
  const badgeCount = attention ? pendingItems.reduce((sum, a) => sum + a.count, 0) : unreadCount;

  // Surface the bell count outside the tab: title prefix "(3) ..." and, when
  // installed as a web app, the Dock/taskbar badge (Badging API; no-op elsewhere).
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\) /, '');
    document.title = badgeCount > 0 ? `(${badgeCount > 99 ? '99+' : badgeCount}) ${base}` : base;
    const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (badgeCount > 0) nav.setAppBadge?.(badgeCount).catch(() => {});
    else nav.clearAppBadge?.().catch(() => {});
    return () => { document.title = base; nav.clearAppBadge?.().catch(() => {}); };
  }, [badgeCount]);

  // Fetch notifications — excludes dismissed rows. Dismissed rows stay
  // in the DB for audit but never render in any list view.
  const fetchNotifications = useCallback(async () => {
    if (!supabase || !profile?.id) return;
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('profile_id', profile.id)
      .is('dismissed_at', null)
      .order('created_at', { ascending: false })
      .limit(30);

    if (data && !error) {
      setNotifications(data as Notification[]);
      setUnreadCount(data.filter((n: any) => !n.is_read).length);
    }
  }, [profile?.id]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Keep the latest fetchNotifications in a ref so the realtime effect
  // doesn't need to re-subscribe when the callback identity changes.
  const fetchNotificationsRef = useRef(fetchNotifications);
  useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications;
  }, [fetchNotifications]);

  // Realtime subscription for new notifications.
  //
  // NOTE: supabase-js v2 returns the SAME channel instance when
  // `.channel(name)` is called twice with the same name. If a previous
  // mount's cleanup hasn't finished (React Strict Mode / Fast Refresh),
  // the second `.on()` throws: "cannot add postgres_changes callbacks
  // after subscribe()". We avoid that by using a unique channel name per
  // effect run and cleaning up the channel instance we own.
  useEffect(() => {
    if (!supabase || !profile?.id) return;

    const channelName = `notifications_${profile.id}_${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `profile_id=eq.${profile.id}`,
        },
        (payload) => {
          const newNotif = payload.new as Notification;
          // Rows muted in-app are inserted pre-dismissed (so push/email still
          // fire) — they must never surface in the bell.
          if (newNotif.dismissed_at) return;
          setNotifications(prev => [newNotif, ...prev].slice(0, 30));
          setUnreadCount(prev => prev + 1);
          fireDesktopNotification({
            title: newNotif.title,
            body: newNotif.body ?? undefined,
            tag: newNotif.id,
            data: { target_type: newNotif.target_type, target_id: newNotif.target_id },
            // Deep-link straight to the relevant page (e.g. a new_match → its matches page),
            // falling back to the notifications list if the target isn't routable.
            onClick: () => navigate(getNotificationRoute(newNotif) || '/notificaciones'),
          });
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `profile_id=eq.${profile.id}`,
        },
        () => {
          // Refetch on updates (mark read) — use the ref so the effect
          // isn't forced to re-subscribe when the callback changes.
          fetchNotificationsRef.current();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  // Position dropdown relative to bell button & close on outside click
  useEffect(() => {
    if (!isOpen) return;
    if (bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const markAsRead = async (id: string) => {
    if (!supabase) return;
    // Optimistic update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      console.error('[NotificationDropdown] markAsRead failed:', error);
      // Revert optimistic update so UI reflects real state
      fetchNotifications();
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    if (!notif.is_read) markAsRead(notif.id);
    // Always close and navigate — unroutable types fall back to the full list
    // so a click never dead-ends with the dropdown stuck open.
    setIsOpen(false);
    navigate(getNotificationRoute(notif) || '/notificaciones');
  };

  // Appeal straight from the "Propiedad retirada de Intercambio" notification —
  // the RPC re-shares the property while a super admin reviews it. Mirrors
  // NotificationsPage.handleAppeal.
  const handleAppeal = async (notif: Notification) => {
    if (!supabase || !notif.target_id) return;
    const message = prompt('Cuéntanos por qué crees que la detección es incorrecta (opcional):') ?? undefined;
    setAppealingId(notif.id);
    const result = await submitShareAppeal(supabase, notif.target_id, message);
    setAppealingId(null);
    alert(result.message);
  };

  const markAllRead = async () => {
    if (!supabase || !profile?.id) return;
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('profile_id', profile.id)
      .eq('is_read', false);
    if (error) {
      console.error('[NotificationDropdown] markAllRead failed:', error);
      fetchNotifications();
    }
  };

  // Soft-delete: flip dismissed_at. Row stays in the DB but disappears
  // from every list view. Also decrements the unread badge if the
  // dismissed notification was still unread.
  const dismissNotification = async (id: string) => {
    if (!supabase) return;
    const prev = notifications.find(n => n.id === id);
    setNotifications(cur => cur.filter(n => n.id !== id));
    if (prev && !prev.is_read) {
      setUnreadCount(c => Math.max(0, c - 1));
    }
    const { error } = await supabase
      .from('notifications')
      .update({ dismissed_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      console.error('[NotificationDropdown] dismiss failed:', error);
      fetchNotifications();
    }
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        ref={bellRef}
        onClick={() => setIsOpen(prev => !prev)}
        className={clsx(
          "relative p-2.5 rounded-xl border transition-all",
          isOpen
            ? "text-white border-transparent"
            : isLight
              ? "bg-slate-100 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-200"
              : "bg-white/5 border-white/5 text-slate-400 hover:text-white hover:bg-white/10"
        )}
        style={isOpen ? {
          backgroundColor: 'var(--accent-600)',
          borderColor: 'var(--accent-400)',
          boxShadow: '0 0 20px -4px var(--accent-glow)'
        } : undefined}
      >
        <Bell size={20} />
        {badgeCount > 0 && (
          <span className={clsx(
            "absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-black text-white bg-rose-500 border-2 px-1",
            isLight ? "border-white" : "border-[#020617]"
          )}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {/* Dropdown (portal to body so it's above everything) */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: dropdownPos.top, right: dropdownPos.right }}
          className={clsx(
            "w-96 max-h-[70vh] flex flex-col rounded-2xl shadow-2xl border overflow-hidden z-[9999] animate-in fade-in slide-in-from-top-2 duration-200",
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-white/10"
          )}>
          {/* Header */}
          <div className={clsx(
            "flex items-center justify-between px-5 py-4 border-b",
            isLight ? "border-slate-100" : "border-white/5"
          )}>
            <h3 className={clsx("text-sm font-black", isLight ? "text-slate-900" : "text-white")}>
              Notificaciones
              {unreadCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                  {unreadCount}
                </span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-[11px] font-bold text-indigo-500 hover:text-indigo-400 transition-colors"
              >
                <CheckCheck size={14} />
                Marcar todas
              </button>
            )}
          </div>

          {/* Pending queues (super admin) — the breakdown of the bell total */}
          {pendingItems.length > 0 && (
            <div className={clsx("px-3 py-2 border-b", isLight ? "border-slate-100" : "border-white/5")}>
              <p className="px-2 pt-1 pb-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">Pendientes</p>
              {pendingItems.map(item => (
                <button
                  key={item.path}
                  onClick={() => { setIsOpen(false); navigate(item.path); }}
                  className={clsx(
                    "w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[13px] font-semibold transition-colors",
                    isLight ? "text-slate-700 hover:bg-slate-50" : "text-slate-200 hover:bg-white/5",
                  )}
                >
                  <span>{item.label}</span>
                  <span className="min-w-[20px] px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold text-center">
                    {item.count > 99 ? '99+' : item.count}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Bell size={32} className="text-slate-400 mb-3" />
                <p className={clsx("text-sm font-medium", isLight ? "text-slate-500" : "text-slate-400")}>
                  Sin notificaciones
                </p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={clsx(
                    "group flex items-start gap-3 px-5 py-4 border-b transition-colors cursor-pointer",
                    isLight
                      ? notif.is_read
                        ? "border-slate-50 hover:bg-slate-50"
                        : "bg-indigo-50/50 border-indigo-100/50 hover:bg-indigo-50"
                      : notif.is_read
                        ? "border-white/5 hover:bg-white/5"
                        : "bg-indigo-500/5 border-indigo-500/10 hover:bg-indigo-500/10"
                  )}
                >
                  {/* Icon */}
                  <div className={clsx(
                    "w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5",
                    isLight ? "bg-slate-100" : "bg-white/5"
                  )}>
                    {typeIcons[notif.type] || <Bell size={16} className="text-slate-400" />}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={clsx(
                      "text-[13px] leading-tight mb-0.5",
                      notif.is_read
                        ? isLight ? "text-slate-600 font-medium" : "text-slate-400 font-medium"
                        : isLight ? "text-slate-900 font-bold" : "text-white font-bold"
                    )}>
                      {notif.title}
                    </p>
                    {notif.body && (
                      <p className={clsx("text-[11px] leading-snug line-clamp-2", isLight ? "text-slate-500" : "text-slate-500")}>
                        {notif.body}
                      </p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                    {notif.type === 'share_compliance' && notif.target_id && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleAppeal(notif); }}
                        disabled={appealingId === notif.id}
                        className={clsx(
                          "mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-colors disabled:opacity-50",
                          isLight
                            ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                            : "bg-amber-500/10 text-amber-400 hover:bg-amber-500/20",
                        )}
                      >
                        {appealingId === notif.id
                          ? <Loader2 size={11} className="animate-spin" />
                          : <ShieldAlert size={11} />}
                        Apelar
                      </button>
                    )}
                  </div>

                  {/* Right column: dismiss button + unread dot */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <button
                      type="button"
                      aria-label="Eliminar notificación"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissNotification(notif.id);
                      }}
                      className={clsx(
                        "opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-rose-500/10",
                        isLight ? "text-slate-400 hover:text-rose-600" : "text-slate-500 hover:text-rose-400",
                      )}
                    >
                      <X size={14} />
                    </button>
                    {!notif.is_read && (
                      <div className="w-2 h-2 rounded-full bg-indigo-500" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer — always visible so the user can reach /notificaciones
              even when the dropdown list is empty. */}
          <div className={clsx(
            "border-t px-5 py-3",
            isLight ? "border-slate-100 bg-slate-50/40" : "border-white/5 bg-white/[0.02]",
          )}>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/notificaciones');
              }}
              className={clsx(
                "w-full text-center text-[11px] font-bold transition-colors",
                isLight ? "text-indigo-600 hover:text-indigo-700" : "text-indigo-400 hover:text-indigo-300",
              )}
            >
              Ver todas las notificaciones
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
